import React, { Component } from 'react';
import { Card, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Fetcher } from '../../../Helpers/fetcher.js';
import { Notify } from '../../../Helpers/notification.js';
import SheetPage from '../components/SheetPage';

const MAX_PAGES = 5;

class SheetComposer extends Component {
	constructor(props) {
		super(props);
		this.pageRefs = {};
		this.state = {
			pages: [{ id: 'page-0', initialDelta: null }],
			sheetName: '',
			visibility: 'ALL',
			loading: this.isEditMode(),
			submitting: false
		};
	}

	isEditMode = () => !!(this.props.match && this.props.match.params && this.props.match.params.id);

	componentDidMount() {
		if (this.props.app.role !== 'ROLE_SADMIN' && this.props.app.role !== 'ROLE_SUB_SADMIN') {
			this.props.history.replace('/');
			return;
		}
		if (this.isEditMode()) {
			this.loadExistingSheet();
		}
	}

	loadExistingSheet = () => {
		const apiPath = this.props.app.apiPath;
		const id = this.props.match.params.id;
		Fetcher.get(apiPath + '/api/obd/sheet/content/' + id)
			.then(res => {
				if (!res || !res.id) {
					Notify.notify('Sheet not found');
					return;
				}
				const contents = [res.content1, res.content2, res.content3, res.content4, res.content5];
				const pages = contents
					.map((content, idx) => ({ content, idx }))
					.filter(p => p.content && p.content.trim() !== '')
					.map(p => ({ id: 'page-' + p.idx, initialDelta: p.content }));

				this.setState({
					sheetName: res.sheetName,
					visibility: res.visibility || 'ALL',
					pages: pages.length > 0 ? pages : [{ id: 'page-0', initialDelta: null }]
				});
			})
			.catch(err => {
				console.log('Error in fetching Sheet Content', err);
				Notify.notify('Error in fetching Sheet Content');
			})
			.finally(() => this.setState({ loading: false }));
	};

	setvalue = (e) => {
		this.setState({ [e.target.name]: e.target.value });
	};

	getPageRef = (id) => {
		if (!this.pageRefs[id]) {
			this.pageRefs[id] = React.createRef();
		}
		return this.pageRefs[id];
	};

	addSheet = () => {
		this.setState(prevState => {
			if (prevState.pages.length >= MAX_PAGES) return null;
			return {
				pages: [...prevState.pages, { id: 'page-' + Date.now(), initialDelta: null }]
			};
		});
	};

	removeSheet = (pageId) => {
		this.setState(prevState => {
			if (prevState.pages.length <= 1) return null;
			delete this.pageRefs[pageId];
			return { pages: prevState.pages.filter(p => p.id !== pageId) };
		});
	};

	handleSubmit = (e) => {
		e.preventDefault();
		if (!this.state.sheetName || this.state.sheetName.trim() === '') {
			Notify.notify('Please enter a sheet name');
			return;
		}

		const firstRef = this.pageRefs[this.state.pages[0].id];
		if (!firstRef || !firstRef.current || firstRef.current.isEmpty()) {
			Notify.notify('Please add some content before submitting');
			return;
		}

		const contentValues = [0, 1, 2, 3, 4].map(idx => {
			const page = this.state.pages[idx];
			if (!page) return '';
			const ref = this.pageRefs[page.id];
			return ref && ref.current ? ref.current.getHTML() : '';
		});

		const data = {
			sheetName: this.state.sheetName,
			visibility: this.state.visibility,
			content1: contentValues[0],
			content2: contentValues[1],
			content3: contentValues[2],
			content4: contentValues[3],
			content5: contentValues[4],
			status: 1,
			createdBy: this.props.app.userId
		};

		const isEdit = this.isEditMode();
		if (isEdit) {
			data.id = Number(this.props.match.params.id);
		}

		const url = this.props.app.apiPath + (isEdit ? '/api/obd/sheet/content/update' : '/api/obd/sheet/content/create');

		this.setState({ submitting: true });
		Fetcher.post(url, { headers: { "Content-type": "application/json" }, method: 'POST', body: JSON.stringify(data) })
			.then(res => {
				Notify.notify(res.message);
				if (res.message && res.message.toLowerCase().includes('success')) {
					const urlPrefix = this.props.app.urlPrefix || '';
					this.props.history.push(`${urlPrefix}/superadmin-api-docs/sheet-content`);
				}
			})
			.catch(err => {
				console.log('Error in submitting Sheet Content', err);
				Notify.notify('Error in submitting Sheet Content');
			})
			.finally(() => this.setState({ submitting: false }));
	};

	render() {
		const { pages, sheetName, visibility, loading, submitting } = this.state;

		if (loading) {
			return (
				<Card>
					<Card.Body className="text-center"><Spinner animation="border" /></Card.Body>
				</Card>
			);
		}

		return (
			<Card>
				<Card.Header>{this.isEditMode() ? 'Edit Sheet' : 'Add Sheet'}</Card.Header>
				<Card.Body>
					<Form onSubmit={this.handleSubmit}>
						<Row className="mb-3">
							<Col sm={4}>
								<Form.Group>
									<Form.Label>Sheet Name</Form.Label>
									<Form.Control name="sheetName" type="text" value={sheetName} onChange={this.setvalue} required />
								</Form.Group>
							</Col>
							<Col sm={4}>
								<Form.Group>
									<Form.Label>Visibility</Form.Label>
									<Form.Control as="select" name="visibility" value={visibility} onChange={this.setvalue}>
										<option value="ALL">Public (All Users)</option>
										<option value="PRIVATE">Private (Only Me)</option>
									</Form.Control>
								</Form.Group>
							</Col>
							<Col sm={4} className="d-flex align-items-end">
								<Form.Group className="mb-0">
									<Button
										variant="secondary"
										type="button"
										onClick={this.addSheet}
										disabled={pages.length >= MAX_PAGES}
									>
										+ Add Sheet
									</Button>
									{pages.length >= MAX_PAGES && (
										<span className="text-muted ml-2">Maximum of {MAX_PAGES} sheets reached.</span>
									)}
								</Form.Group>
							</Col>
						</Row>

						<div className="sheet-canvas-wrapper">
							{pages.map((page, idx) => (
								<div className="sheet-page-wrapper" key={page.id}>
									<SheetPage
										ref={this.getPageRef(page.id)}
										initialDelta={page.initialDelta}
										pageLabel={`Sheet ${idx + 1} of ${MAX_PAGES}`}
										showRemove={pages.length > 1}
										onRemove={() => this.removeSheet(page.id)}
									/>
								</div>
							))}
						</div>

						<Button variant="primary" type="submit" disabled={submitting}>
							{submitting ? 'Submitting...' : 'Submit'}
						</Button>
					</Form>
				</Card.Body>
			</Card>
		);
	}
}

export default SheetComposer;
